import { useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ConfirmDialog } from "primereact/confirmdialog";

import UsersUpdate from "./UserUpdate";
import { IUser, IUserListProps } from "./User.model";
import { userService } from "../../service/User/user.service";
import { Can } from "../../ability/can";
import {
  PERMISSIONS_ACTION,
  PERMISSIONS_MODEL,
} from "../../library/utilities/constants/permissions";

const UserList = ({ openNew, loadUsers, data, loading }: IUserListProps) => {
  const [userDialog, setUserDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const deleteUser = (id: number) => {
    setDeleteId(id);
    setConfirmDialog(true);
  };

  const confirmDeleteUser = async () => {
    if (!deleteId) return;
    await userService.remove(deleteId);

    setConfirmDialog(false);
    loadUsers();
  };

  const actionBodyTemplate = (row: IUser) => {
    return (
      <div className="flex gap-2">
        <Can I={PERMISSIONS_ACTION.USER_CHANGE} a={PERMISSIONS_MODEL.USER}>
          <Button
            label="Edit"
            icon="pi pi-pencil"
            onClick={() => {
              setSelectedUser(row);
              setUserDialog(true);
            }}
          />
        </Can>

        <Can I={PERMISSIONS_ACTION.USER_DELETE} a={PERMISSIONS_MODEL.USER}>
          {!row?.is_superuser && (
            <Button
              label="Delete"
              icon="pi pi-trash"
              severity="danger"
              onClick={() => deleteUser(row.id)}
            />
          )}
        </Can>
      </div>
    );
  };

  const userColumns = [
    {
      field: "username",
      header: "Username",
    },
    {
      field: "first_name",
      header: "First Name",
    },
    {
      field: "last_name",
      header: "Last Name",
    },
    {
      field: "email",
      header: "Email",
    },
    {
      field: "is_active",
      header: "Active",
      body: (row: IUser) => (row.is_active ? "Yes" : "No"),
    },
    {
      field: "group.name",
      header: "Role",
    },
    {
      field: "is_superuser",
      header: "Superuser",
      body: (row: IUser) => (row.is_superuser ? "Yes" : "No"),
    },
    {
      field: "date_joined",
      header: "Joined On",
    },
  ];

  const [globalFilter, setGlobalFilter] = useState("");
  const filteredData = data.filter((user: IUser) => {
    const search = globalFilter.toLowerCase();
    return (
      user.username.toLowerCase().includes(search) ||
      user.email.toLowerCase().includes(search) ||
      (user.first_name && user.first_name.toLowerCase().includes(search)) ||
      (user.last_name && user.last_name.toLowerCase().includes(search))
    );
  });

  return (
    <>
      <Dialog
        visible={userDialog}
        header="Update User"
        modal
        onHide={() => setUserDialog(false)}
        className="w-5">
        {selectedUser && (
          <UsersUpdate
            data={selectedUser}
            setUserDialog={setUserDialog}
            loadUsers={loadUsers}
          />
        )}
      </Dialog>

      {/* User Table */}
      <DataTable
        value={filteredData}
        loading={loading}
        dataKey="id"
        paginator
        rows={10}
        header={
          <div className="flex justify-content-between align-items-center gap-3">
            <h3>User Management</h3>
            <div className="flex">
              <span className="p-input-icon-left mr-3">
                <i className="pi pi-search" />
                <input
                  type="text"
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  placeholder="Search by username, email, name"
                  className="p-inputtext p-component"
                />
              </span>
              <Can I={PERMISSIONS_ACTION.USER_ADD} a={PERMISSIONS_MODEL.USER}>
                <Button label="New User" onClick={openNew} />
              </Can>
            </div>
          </div>
        }>
        {userColumns.map((col) => (
          <Column
            sortable
            key={col.field}
            field={col.field}
            header={col.header}
            body={col.body}
          />
        ))}

        <Column header="Actions" body={actionBodyTemplate} />
      </DataTable>

      {/* Delete Confirmation */}
      <ConfirmDialog
        visible={confirmDialog}
        onHide={() => setConfirmDialog(false)}
        message="Are you sure you want to delete this user?"
        header="Delete Confirmation"
        icon="pi pi-exclamation-triangle"
        accept={confirmDeleteUser}
        reject={() => setConfirmDialog(false)}
      />
    </>
  );
};

export default UserList;
